/**
 * Helpers for Customer Files factory methods
 */

import type { IBaseCustomer } from "../../BaseCustomerFactory";
import type { File } from "le-coffre-resources/dist/Customer";
import type { IRef } from "@Front/Api/BaseApiService";
import type { QueryPayload } from "../../../../types";
import type { IGetFilesparams, IPutFilesParams } from "../Files";
import { downloadMultipleFiles, downloadAggregatedCertificate as downloadAggregatedCertificateHelper } from "../../../helpers/fileDownloadHelpers";
import { buildFilesUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";
import { makeHttpRequest } from "@Front/Api/helpers/httpRequestHelpers";
import type { DepositAttemptPayload } from "@Front/Components/DesignSystem/DepositOtherDocument/helpers/depositAttemptReporter";

/**
 * Create GET method for files
 */
export function createGetMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (q: IGetFilesparams): Promise<File[]> => {
		const url = new URL(baseUrl);
		const query = { q };
		if (q) baseCustomer.appendJsonSearchParams(url, query);
		try {
			const files = await baseCustomer.getRequest<File[]>(url);
			return files;
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for files
 */
export function createPostMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (body: FormData): Promise<File> => {
		const url = new URL(baseUrl);
		try {
			return await baseCustomer.postRequestFormData<File>(url, body);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get upload link method
 */
export function createGetUploadLinkMethod(baseUrl: string) {
	return (uid: string): string => {
		return baseUrl.concat(`/download/${uid}`);
	};
}

/**
 * Create get download link method
 */
export function createGetDownloadLinkMethod(baseUrl: string) {
	return (uid: string, withCertificate = false): string => {
		const url = new URL(baseUrl.concat(`/download/${uid}`));
		if (withCertificate) {
			url.searchParams.set("with_certificate", "true");
		}
		return url.toString();
	};
}

/**
 * Create GET by UID method for files
 */
export function createGetByUidMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const query = { q };
		if (q) baseCustomer.appendJsonSearchParams(url, query);
		try {
			const file = await baseCustomer.getRequest<File>(url);
			return file;
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for files
 */
export function createPutMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, body: IPutFilesParams): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildFilesUpdatePayload(body);
			return await baseCustomer.putRequest<File>(url, payload);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for files
 */
export function createDeleteMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseCustomer.deleteRequest<File>(url);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create download method for files
 */
export function createDownloadMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, withCertificate = false, ref?: IRef): Promise<Blob> => {
		const url = new URL(baseUrl.concat(`/download/${uid}`));
		if (withCertificate) {
			url.searchParams.set("with_certificate", "true");
		}
		try {
			return await baseCustomer.getRequest<Blob>(url, { ref });
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Telemetry of deposit attempts (picker / drop / confirm / upload). Background call: no toast,
 * no session teardown on 401 (guest / third-party tokens), never calls baseCustomer.onError.
 */
export function createReportDepositAttemptMethod(baseUrl: string) {
	return async (payload: DepositAttemptPayload): Promise<void> => {
		const url = new URL(baseUrl.concat("/deposit-attempts"));
		await makeHttpRequest<void>({
			url,
			method: "POST",
			body: payload,
			background: true,
			skipSessionFailureOnAuthError: true,
		});
	};
}

/**
 * Create download multiple method for files
 */
export function createDownloadMultipleMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (fileUids: string[], includeAnchors = true, folderNumber?: string, folderName?: string): Promise<Blob> => {
		return downloadMultipleFiles(baseCustomer, baseUrl, { fileUids, includeAnchors, folderNumber, folderName });
	};
}

/**
 * Create download aggregated certificate method for files
 */
export function createDownloadAggregatedCertificateMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (documentUids: string[], folderUid: string): Promise<Blob> => {
		return downloadAggregatedCertificateHelper(baseCustomer, baseUrl, documentUids, folderUid);
	};
}
